import { defineCopy } from '@/copy';

/** combine strings. Keep the voice: short, funny, no em dashes. */
export const copy = defineCopy('combine', {
  tile: 'Five drills. Everyone is watching.',

  hub: {
    title: 'Combine',
    line: 'Five drills, then Draft Night. Everybody leaves with a card.',
    empty: "Nobody's tested yet. Lace up.",
    drills: 'The drills',
    core: 'Core',
    bonus: 'Bonus',
    start: 'Start',
    again: 'Run it again',
    doneOf: '{done} of {total} done',
    nobodyYet: 'Nobody yet',
    shots: '{n} shots each',
    games: '{n} games each',
    resumeTitle: 'Still on the clock',
    resumeBody: '{level}, {n} shooters. Pick up where you left off.',
    resume: 'Resume',
    draftTitle: 'Draft Night',
    draftBody: '{n} rookies have their numbers. Time to open the packs.',
    draftBodyOne: 'One rookie has their numbers. A one-man draft class.',
    draftCta: 'Open the packs',
    draftWait: 'Finish the four core drills and the cards come out.',
    rookies: '{done} of {total} have a card',
    loadFailed: "Couldn't load the combine. Pull to retry, or walk toward signal.",
    waiting: '{n} shots waiting for signal',
  },

  levels: {
    arc: {
      name: 'The Arc',
      blurb: 'Five spots behind the line, five shots from each.',
      intro: 'Twenty-five threes. No pressure. (There is pressure.)',
    },
    midrange: {
      name: 'Midrange Tour',
      blurb: 'Baseline, elbow, free throw line, elbow, baseline. Five shots each.',
      intro: 'The shot nobody practices. Today you do.',
    },
    layup: {
      name: 'Layup Line',
      blurb: 'Five from the left side, five from the right. No dunking.',
      intro: 'Ten layups. The easiest ten shots you will miss.',
    },
    charity: {
      name: 'Charity Stripe',
      blurb: 'Ten free throws. The second set has the pressure on.',
      intro: 'Nobody is guarding you. That is the problem.',
    },
    iso: {
      name: 'Iso Gauntlet',
      blurb: 'Three 1v1 games to 3. Real games, real Elo.',
      intro: 'Three opponents. Games to 3. Honor system.',
    },
  },

  setup: {
    title: 'Set it up',
    who: 'Who is shooting?',
    whoHint: 'Everyone who is here. Latecomers join the next level.',
    doneTag: 'Done already',
    rotation: 'Rotation',
    bySpot: 'By spot',
    bySpotHint: 'Everyone shoots a spot, then the group walks over. Less walking.',
    byPlayer: 'By player',
    byPlayerHint: 'One shooter does the whole level, then the next. Slower, quieter.',
    recording: 'Recording',
    shotByShot: 'Shot by shot',
    shotByShotHint: 'Tap MAKE or MISS for every shot.',
    fast: 'Fast entry',
    fastHint: 'Shoot first, then type the makes after each spot.',
    order: 'Shooting order',
    orderHint: 'Shuffled. Tap an arrow to move someone.',
    shuffle: 'Shuffle',
    up: 'Move up',
    down: 'Move down',
    tipOff: 'Tip off',
    needOne: 'Pick at least one shooter.',
    needTwo: 'The Gauntlet needs two players at least.',
    failed: "Couldn't start that. Give it one more try.",
    back: 'Back to the combine',
  },

  rec: {
    back: 'Pause',
    backLabel: 'Back to the combine. Your shots are saved.',
    sun: 'Sun mode',
    stepSpot: 'Spot {n} of {total}',
    stepSet: 'Set {n} of {total}',
    upNow: 'Up now',
    make: 'Make',
    miss: 'Miss',
    undo: 'Undo',
    undoNone: 'Nothing to undo',
    undoLine: 'Undo: {result}, {spot}',
    undoBlock: 'Undo: {n} shots at {spot}',
    resultMake: 'make',
    resultMiss: 'miss',
    total: '{makes}/{att} ({pct})',
    totalNone: 'No shots yet',
    upNext: 'Up next: {names}',
    upNextLast: 'Last shooter. Make it count.',
    pressure: 'Pressure set. Somebody is talking behind you.',
    skip: 'Skip for now',
    skipped: '{name} goes to the back of the line.',
    more: 'More',
    finishHere: 'Finish here',
    finishHereBody: 'Shots so far are kept. Anyone with too few shots will not get the level checked off.',
    finishHereCta: 'Finish the level',
    bin: 'Bin this run',
    binBody: 'None of these shots count. Use it when the whole thing was a mistake.',
    binCta: 'Hold to bin it',
    cancel: 'Keep shooting',
    advance: 'Nice. {name}, you are up. {spot}.',
    advanceSpot: '{spot} done. Walk to {next}.',
    spotDone: '{spot} is done',
    spotBoard: 'Best at {spot}',
    flame: 'On fire',
    nextSpot: 'On to {spot}',
    lastSpot: 'That is everyone. Show the results',
    fastTitle: 'Makes out of {n}',
    fastDone: 'Logged, next shooter',
    fastMinus: 'One fewer make',
    fastPlus: 'One more make',
    waiting: '{n} shots waiting for signal',
    wakeOff: 'Keep the screen on yourself',
    notFound: 'This drill is not on this phone.',
    notFoundBody: 'It may have been made on another one. Open it from the combine.',
    lines: {
      zero: ['0 for {n}. The rim has filed a complaint.', '0 for {n}. The ball is still looking for the basket.'],
      perfect: ['{n} for {n}. Somebody call the league.', '{n} for {n}. Suspiciously good.'],
      good: ['Money. Keep going.', 'That is a real jumper.'],
      ok: ['Fine. Fine is fine.', 'The rim was home sometimes.'],
    },
    allDone: 'That is everyone. Nice work.',
    saving: 'Saving the level',
    parked: "A few shots didn't count",
    parkedBody: 'The server turned down {n} of them. The totals shown here are what it kept.',
  },

  results: {
    title: '{level} results',
    sub: 'Bars race. Egos follow.',
    rank: '#{n}',
    tied: 'T{n}',
    percentile: 'Better than {n} percent of this group',
    percentileTop: 'Top of this group',
    streak: '{n} in a row',
    brick: '1 brick',
    bricks: '{n} bricks',
    hot: 'Hot',
    cold: 'Cold',
    bestSpot: 'Best: {spot}',
    worstSpot: 'Coldest: {spot}',
    shotChart: 'Shot chart',
    pickPlayer: 'Tap a name to see where they shoot',
    group: 'The crew shot {makes} of {att}, {pct}',
    nobody: 'No shots in this one. A tough crowd.',
    partial: 'Not finished ({att} of {need})',
    ratings: 'Ratings',
    ratingsWait: 'Ratings update when you get signal.',
    saving: 'Saving your shots. The numbers land when the phone finds signal.',
    nextLevel: 'Next: {level}',
    coreDone: 'That is the whole core combine.',
    draft: 'Draft Night',
    hub: 'Back to the combine',
    all: 'Everyone',
    missing: 'Results are not on this phone yet.',
    openGame: 'Open the game',
  },

  iso: {
    title: 'Iso Gauntlet',
    sub: 'Everyone plays three. Games to 3, self-scored, real Elo.',
    round: 'Round {n}',
    play: 'Play',
    live: 'Live',
    final: 'Final',
    queued: 'Not started',
    score: '{a} {sa}, {b} {sb}',
    progress: '{n} of {total} games played',
    each: '{name}: {n} of {goal}',
    finish: 'Finish the Gauntlet',
    finishBody: 'Anything unplayed stays unplayed. Played games keep counting.',
    finishCta: 'Finish it',
    cancel: 'Keep going',
    starting: 'Starting the game',
    failed: "Couldn't start that game. Try again.",
    open: 'Open',
    all: 'All games',
    openPad: 'Keep score',
    rematch: 'Rematch',
    notFound: 'This Gauntlet is not on this phone.',
    notFoundBody: 'It may have been started on another one.',
    gamesDone: 'All games played. Time to finish it.',
  },

  draft: {
    title: 'Draft Night',
    intro: '{n} rookies, {n} packs, one rule: no crying.',
    introOne: 'One rookie, one pack. A short night.',
    start: 'Start the show',
    pick: 'Pick {n} of {total}',
    next: 'Next pick',
    last: 'Last pack',
    skip: 'Skip to the end',
    waiting: 'The cards are still in the oven.',
    waitingBody: 'The ratings come in a few seconds after the last shot.',
    nobody: 'Nobody has finished the core combine yet.',
    nobodyBody: 'Four drills each, then the packs open.',
    classTitle: 'The draft class',
    classLine: 'Screenshot this. You will want proof.',
    share: 'Save the card',
    done: 'Done',
    hub: 'Back to the combine',
    who: 'Who is in the draft?',
    whoHint: 'Everyone with the core combine done. Untick anyone you want to leave out.',
    scout: {
      three: 'Elite at shooting when nobody is guarding him. So, always.',
      mid: 'Lives between the elbows. Pays rent there.',
      fin: 'Finishes at the rim like the rim owes him money.',
      ft: 'Calm at the line. Annoyingly calm.',
      playmaking: 'Sees passes that are not there yet.',
      def: 'Locks the room. Possibly the building.',
      reb: 'Every missed shot is his.',
      hustle: 'Dives first, asks later.',
      clu: 'Quiet all night, loud at the end.',
      unrated: 'A mystery box. Handle with care.',
      even: 'No weak spots. No loud spots either.',
    },
  },

  ideas: {
    title: 'What now?',
    sub: 'Three ideas, one tap each.',
    use: 'Use this',
    balanced: {
      title: 'Balanced 2v2s',
      blurb: '{a} against {b} on paper. A coin flip with feelings.',
      blurbSit: '{a} against {b} on paper. {n} sit this one out and heckle.',
    },
    roundRobin: {
      title: '1v1 round robin',
      blurb: '{n} players, {g} games to 3. Everybody plays everybody.',
    },
    bracket: {
      title: 'Bracket night',
      blurb: '{n} players seeded by card, {g} games. One champion, many excuses.',
    },
  },

  card: {
    title: 'Combine',
    empty: "Nobody's tested yet. Lace up.",
    cta: 'Start the combine',
    rookies: '{n} still need a card.',
    rookiesOne: 'One rookie still needs a card.',
    allDone: 'Everyone has a card.',
    draft: 'Run Draft Night',
    resume: 'Resume {level}',
    resumeLine: 'The clock never stopped. Pick it back up.',
    open: 'Open the combine',
    ring: '{done} of {total} combined',
  },

  errors: {
    somebody: 'Somebody',
    generic: 'Something tripped. Try again.',
  },
});
